import {
  ApiErrorResponse,
  confirmationHeaders,
  CustomPagination,
  DetailLayout,
  ErrorAlertWithDrawer,
  FORM_SUBMIT_NOT_COMMITTED,
  Form,
  FormAlerts,
  FormRef,
  InputDataDetailsType,
  InputDataType,
  InputDataValidFormDataType,
  Padding,
  SubmittedDataVersionType,
  TotalEntries,
  useInputDataDetailsCommentsMutation,
  useQueryParams,
  SampleCommentSchema,
  InputDataDetailsKey,
  SampleCommentType,
  BookmarkCreate,
  InfoIconUrl,
  ISODate,
  DraftAutosaver,
  EditSessionFormRowsContextProvider,
  useEditSessionContext,
  FormSubmitHandler,
  InputDataValidFormDataSchema,
  useAlertContext,
} from '@atlas-frontend/sdk';
import { RefObject, useCallback, useEffect, useMemo, useRef } from 'react';
import { Alert, PaginationProps } from 'antd';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from '../InputDataSubjectDetail.translations.json';
import styled from 'styled-components';
import { InputDataSubjectHeader } from '../InputDataSubjectHeader';
import { InputDataDetailsPanel } from '../InputDataDetailsPanel';
import { TableFilterSortProps } from '../../table/InputDataViewTable';
import { useInputDataDetailsStoreValue } from '../InputDataDetailsContext';
import { InputDataDescriptionInfo, InputDataVersionInfo } from '../types';
import { UseFieldArrayReturn } from 'react-hook-form';
import { useInputDataActions } from '../useInputDataActions';
import { InputDataSubjectActions } from '../InputDataSubjectActions';
import type { SortingState } from '@tanstack/react-table';
import { InputDataSubjectDetailLeftSideHeader } from '../leftSide/InputDataSubjectDetailLeftSideHeader';
import { InputDataSubjectDetailLeftSideContent } from '../leftSide/InputDataSubjectDetailLeftSideContent';
import { InputDataSubjectDetailLeftSideFooter } from '../leftSide/InputDataSubjectDetailLeftSideFooter';
import { useInputDataComments } from '../view/useInputDataComments';
import { OutputSnapshotVersionType } from '@atlas-frontend/sdk';

const { addTranslations, useTranslation } = i18nNsFactory(
  'input-data-sample-detail',
);
addTranslations(translations);

interface InputDataSubjectFormProps {
  groupId?: string;
  valueDate: ISODate;
  subject: string;
  initialFilters?: Partial<Record<string, any>>;
  initialSort?: SortingState;
  initialPagination?: PaginationProps;
  inputDataDetailsType?: InputDataDetailsType;
  descriptionData: InputDataDescriptionInfo;
  detailsData?: InputDataType;
  detailsError?: ApiErrorResponse | null;
  subjectNameMappings?: Record<string, string>;
  errorType?: string;
  selectedSubmittedDataId?: number;
  onSelectSubmittedDataId?: (submittedDataId: number) => void;
  output?: OutputSnapshotVersionType;
  selectedVersion?: SubmittedDataVersionType;
  versionData: InputDataVersionInfo;
  formRef?: RefObject<FormRef<any> | null>;
  canManage: boolean;
  onReload: () => Promise<any>;
  isLoading: boolean;
  onAddComment: (comment: SampleCommentType) => void;
  paginationProps?: PaginationProps;
  tableProps?: TableFilterSortProps;
}

const defaultInputDataFormData = {};

export function InputDataSubjectDetailsForm(props: InputDataSubjectFormProps) {
  const {
    groupId,
    inputDataDetailsType,
    detailsData,
    descriptionData,
    onReload,
  } = props;
  const { t } = useTranslation();

  const { updateRow, commitEdits, discardEdits } = useEditSessionContext();
  const { setSuccess } = useAlertContext();

  const ref: RefObject<FormRef<any> | null> = useRef(null);
  const isCommittedRef = useRef(false);
  const fieldArrayRef: RefObject<
    ((name: string) => UseFieldArrayReturn<any, any, any>) | null
  > = useRef(null);
  const { mutateAsync: mutateCommentsAsync } =
    useInputDataDetailsCommentsMutation();
  const [mutationErrors] = useInputDataDetailsStoreValue('mutationErrors');
  const [savedVersion] = useInputDataDetailsStoreValue('savedVersion');
  const [segment] = useInputDataDetailsStoreValue('segment');
  const [mode] = useInputDataDetailsStoreValue('mode');
  const isCommentsTab = useMemo(() => {
    return segment === 'comments';
  }, [segment]);
  const isEditMode = mode === 'edit';

  const { onClear, onError, onSuccess, isLoading, onLoad, setIsLoading } =
    useInputDataActions(onReload);

  const handleEditSessionError = useCallback(
    (error: unknown) => onError(error as ApiErrorResponse, 'save'),
    [onError],
  );

  const {
    valueDate,
    dataSubjectCode,
    sampleComments: comments = [],
  } = inputDataDetailsType ?? {};
  const { id: descriptionId } = descriptionData?.description ?? {};
  const version = props.versionData.version;
  const versionId = props.selectedVersion?.id ?? undefined;

  useEffect(() => {
    if (!savedVersion) return;
    const { rows, comments } = savedVersion;

    if (rows) {
      const fieldArray = fieldArrayRef.current?.('rows');
      if (fieldArray) {
        const { replace } = fieldArray;
        replace(rows);
      } else {
        ref.current?.form.setValue('rows', rows);
      }
    }

    if (comments) {
      ref.current?.form.setValue(
        'comments',
        comments.map(SampleCommentSchema.parseFormData),
      );
    }

    ref.current?.form.reset(undefined, {
      keepValues: true,
      keepDirty: false,
    });
    ref?.current?.supressBlock();
  }, [savedVersion]);

  // Sync comments when underlying data changes (e.g. new comment added)
  useEffect(() => {
    if (!inputDataDetailsType?.sampleComments) return;

    ref.current?.form.setValue(
      'comments',
      inputDataDetailsType.sampleComments.map(
        SampleCommentSchema.parseFormData,
      ),
      { shouldDirty: false },
    );
  }, [inputDataDetailsType?.sampleComments]);

  const keyParams: InputDataDetailsKey = useMemo(
    () => ({
      descriptionId: descriptionId?.toString(),
      groupId,
      valueDate: valueDate ?? '',
      subject: dataSubjectCode ?? '',
      dataStatus: version === 'draft' ? 'draft' : 'active',
      versionId,
    }),
    [dataSubjectCode, descriptionId, groupId, valueDate, version, versionId],
  );

  const handleInputDataSubmit: FormSubmitHandler = async (_data, confirm) => {
    onLoad();
    try {
      if ((await commitEdits(confirm)) === 'blocked') {
        setIsLoading(false);
        return FORM_SUBMIT_NOT_COMMITTED;
      }

      // Data is reloaded by the session close handler, which opens a new edit session
      isCommittedRef.current = true;
      onClear();
      setSuccess();
    } catch (error) {
      handleEditSessionError(error);
      return FORM_SUBMIT_NOT_COMMITTED;
    }
  };

  const handleCommentsSubmit = useCallback(
    (formData: InputDataValidFormDataType, confirm: boolean) => {
      const { comments } = formData;
      onLoad();
      mutateCommentsAsync({
        id: descriptionId ?? 0,
        data: {
          comments,
          params: keyParams,
        },
        headers: confirmationHeaders(confirm),
      })
        .then(onSuccess)
        .catch(onError);
    },
    [onLoad, mutateCommentsAsync, descriptionId, keyParams, onSuccess, onError],
  );

  useEffect(() => {
    // Sync form with new data
    const form = ref.current?.form;
    if (detailsData && form) {
      // After a save, the rows of the new edit session are the new baseline, so the form is not dirty
      const isCommitted = isCommittedRef.current;
      isCommittedRef.current = false;
      form.reset(
        { ...form.getValues(), rows: detailsData.rows },
        { keepDefaultValues: !isCommitted, keepErrors: true },
      );
      if (isCommitted) {
        ref.current?.supressBlock();
      }
    }
  }, [detailsData, ref]);

  // Ref keeps the latest session, as a new one is opened after each save
  const discardEditsRef = useRef(discardEdits);
  discardEditsRef.current = discardEdits;
  useEffect(() => {
    const handleEndSession = () => {
      void discardEditsRef.current(() => {});
    };
    return () => handleEndSession();
    // Has to be empty.
  }, []);

  return (
    <Form
      schema={InputDataValidFormDataSchema}
      defaultValues={{
        ...detailsData,
        comments: comments.map(SampleCommentSchema.parseFormData),
      }}
      onSubmit={isCommentsTab ? handleCommentsSubmit : handleInputDataSubmit}
      formRef={ref}
      fieldArrayRef={fieldArrayRef}
      serverErrors={mutationErrors?.errors}
    >
      <EditSessionFormRowsContextProvider
        fieldArrayName="rows"
        defaultRow={defaultInputDataFormData}
        pagination={props.paginationProps}
        pageData={detailsData}
        onValidationErrors={handleEditSessionError}
        onValidationErrorsResolved={onClear}
        onOperationError={handleEditSessionError}
      >
        {isEditMode && (
          <DraftAutosaver fieldArrayName="rows" updateRow={updateRow} />
        )}
        <InputDataSubjectFormInner
          {...props}
          errorType={
            mutationErrors?.type && mutationErrors?.type !== 'save'
              ? t(`mutation.error.${mutationErrors?.type}`)
              : undefined
          }
          formRef={ref}
          isLoading={isLoading || props.isLoading}
        />
      </EditSessionFormRowsContextProvider>
    </Form>
  );
}

function ViewAlerts() {
  const [mutationErrors, setMutationErrors] =
    useInputDataDetailsStoreValue('mutationErrors');
  const { t } = useTranslation();

  if (!mutationErrors) {
    return null;
  }

  return (
    <ErrorAlertWithDrawer
      validationMessagesMap={mutationErrors.errors}
      errorAction={t('action.delete')}
      closable={true}
      onClose={() => setMutationErrors(undefined)}
    />
  );
}

function InputDataSubjectFormInner(props: InputDataSubjectFormProps) {
  const {
    inputDataDetailsType,
    descriptionData,
    detailsData,
    selectedVersion,
    versionData,
    subjectNameMappings,
  } = props;
  const [{ outputDataVersionId }] = useQueryParams();
  const { description } = descriptionData;
  const [mode] = useInputDataDetailsStoreValue('mode');
  const { segment, commentsTotal, inputComment, dataLoadingError } =
    useInputDataComments(
      inputDataDetailsType,
      selectedVersion,
      props.detailsError,
    );

  return (
    <DetailLayout
      infoIconUrl={InfoIconUrl.INPUT_DATA_SUBJECT_DETAIL}
      breadCrumb={
        <InputDataSubjectHeader
          data={inputDataDetailsType}
          descriptionData={descriptionData}
          selectedSubmittedDataId={props.selectedSubmittedDataId}
          onSelectSubmittedDataId={props.onSelectSubmittedDataId}
          output={props.output}
          versionData={versionData}
          onReload={props.onReload}
          isLoading={props.isLoading}
        />
      }
      bookmarkCreate={
        <BookmarkCreate
          excludeUrlParams={!outputDataVersionId ? ['date'] : []}
          includeUrlParams={
            !outputDataVersionId
              ? [{ key: 'periodSkipLastValueDate', value: 'true' }]
              : []
          }
        />
      }
      headerConfig={{
        actions: (
          <InputDataSubjectActions
            data={inputDataDetailsType}
            descriptionData={descriptionData}
            columnCount={detailsData?.columns?.length}
            versionData={versionData}
            versionId={selectedVersion?.id}
            canManage={props.canManage}
            onReload={props.onReload}
            isLoading={props.isLoading}
          />
        ),
      }}
      side={{
        header: (
          <InputDataSubjectDetailLeftSideHeader
            description={description}
            inputDataDetailsType={inputDataDetailsType}
          />
        ),
        content: (
          <InputDataSubjectDetailLeftSideContent
            inputDataDetailsType={inputDataDetailsType}
            output={props.output}
            selectedSubmittedDataId={props.selectedSubmittedDataId}
            selectedVersion={selectedVersion}
            versionData={versionData}
            inputComment={inputComment}
          />
        ),
        footer: (
          <InputDataSubjectDetailLeftSideFooter
            description={description}
            inputDataDetailsType={inputDataDetailsType}
            selectedVersion={selectedVersion}
            inputComment={inputComment}
          />
        ),
      }}
      footerContent={
        <FooterWrapper>
          <TotalEntries
            count={
              segment === 'data'
                ? (props.paginationProps?.total ?? 0)
                : commentsTotal
            }
          />
          {segment === 'data' && props.paginationProps && (
            <FooterPagination {...props.paginationProps} />
          )}
        </FooterWrapper>
      }
    >
      {mode === 'edit' && <FormAlerts errorAction={props.errorType} />}
      {mode === 'view' && <ViewAlerts />}
      {dataLoadingError ? (
        <Alert type="error" message={dataLoadingError} />
      ) : (
        <Padding>
          <InputDataDetailsPanel
            data={detailsData}
            comments={inputDataDetailsType?.sampleComments ?? []}
            date={props.valueDate}
            description={description}
            descriptions={inputDataDetailsType?.descriptions ?? []}
            sampleId={inputDataDetailsType?.sampleId ?? undefined}
            loadingStatus={
              versionData.version === 'output'
                ? !props.output
                  ? 'NO_OUTPUT_SELECTED'
                  : undefined
                : inputDataDetailsType?.loadingStatus
            }
            subject={props.subject}
            version={versionData.version}
            groupId={inputDataDetailsType?.dataObjectGroupId ?? undefined}
            formRef={props.formRef}
            isLoading={props.isLoading}
            onAddComment={props.onAddComment}
            tableProps={props.tableProps}
            subjectNameMappings={subjectNameMappings}
          />
        </Padding>
      )}
    </DetailLayout>
  );
}

const FooterPagination = styled(CustomPagination)`
  margin-left: auto;
`;

const FooterWrapper = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-right: 20px;
  width: 100%;
`;
